import { useEffect, type RefObject } from "react";

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Focuses the first focusable element on open, traps Tab/Shift+Tab inside the
 *  container, and restores focus to whatever was focused before on close/unmount. */
export function useFocusTrap(active: boolean, containerRef: RefObject<HTMLElement>) {
    useEffect(() => {
        if (!active) return;
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const container = containerRef.current;
        const firstFocusable = container?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        (firstFocusable ?? container)?.focus();

        const handleTab = (e: KeyboardEvent) => {
            if (e.key !== "Tab" || !container) return;
            const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", handleTab);
        return () => {
            document.removeEventListener("keydown", handleTab);
            previouslyFocused?.focus();
        };
    }, [active]);
}
